import { useCallback, useEffect, useRef, useState, type ReactElement, type ReactNode } from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "./Tooltip";

/**
 * Muestra `label` en un tooltip SOLO cuando `children` está REALMENTE truncado
 * (`scrollWidth > clientWidth`, medido en el elemento o en cualquiera de sus
 * descendientes con overflow oculto —ej. `truncate`—), no por un heurístico de
 * longitud de caracteres: un texto corto en una columna angosta también se corta
 * y debe mostrar el texto completo, y uno largo que sí cabe no debe mostrar un
 * tooltip redundante. Se remide con un `ResizeObserver` (las columnas cambian de
 * ancho al redimensionar o con la barra de scroll espejo de las tablas anchas) y
 * en cada render (capta cambios de contenido al filtrar/paginar).
 *
 * `disabled` fuerza a NO mostrar tooltip (ej. celda sin valor). `children` debe
 * ser un único elemento que reenvíe su ref a su nodo DOM raíz (`Text`, `Stack`…).
 */
export function TruncatedTooltip({
  label,
  disabled,
  children,
}: {
  label: ReactNode;
  disabled?: boolean;
  children: ReactElement;
}) {
  const elRef = useRef<HTMLElement | null>(null);
  const [truncado, setTruncado] = useState(false);

  const setRef = useCallback((node: HTMLElement | null) => {
    elRef.current = node;
  }, []);

  const medir = useCallback(() => {
    const el = elRef.current;
    if (!el) return;
    const nodos: HTMLElement[] = [el, ...Array.from(el.querySelectorAll<HTMLElement>("*"))];
    setTruncado(nodos.some((n) => n.scrollWidth > n.clientWidth + 1));
  }, []);

  // Re-mide en cada render (capta cambios de contenido). El bail-out de setState
  // con el mismo valor evita renders extra.
  useEffect(() => {
    medir();
  });

  // Observa cambios de tamaño una sola vez (resize de la ventana / columna).
  useEffect(() => {
    const el = elRef.current;
    if (!el) return;
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => ro.disconnect();
  }, [medir]);

  if (disabled) return <>{children}</>;

  return (
    <Tooltip>
      <TooltipTrigger asChild ref={setRef}>
        {children}
      </TooltipTrigger>
      {truncado && <TooltipContent className="max-w-90 normal-case">{label}</TooltipContent>}
    </Tooltip>
  );
}
